import React, { useMemo, useState } from "react";
import {
  AlertCircle, CalendarDays, CheckCircle2, Clock, Download, Loader2, Plus,
  RefreshCw, Search, Upload,
} from "lucide-react";
import HrModal from "../components/HrModal";
import {
  useHrGetAttendanceQuery,
  useHrGetAttendanceSummaryQuery,
  useHrGetStaffQuery,
  useHrImportAttendanceMutation,
  useHrSaveAttendanceMutation,
} from "../api/hrApi";
import {
  ATTENDANCE_STATUSES, attendanceColor, errMsg, fmtDate, fmtMinutes, fmtTime, monthRange, staffName, todayIso,
} from "../utils/hr";

type View = "register" | "summary";

/** Attendance register, biometric CSV import, and per-staff monthly totals. */
export default function HrAttendancePage() {
  const thisMonth = monthRange();

  const [view, setView] = useState<View>("register");
  const [from, setFrom] = useState(thisMonth.from);
  const [to, setTo] = useState(thisMonth.to);
  const [adminId, setAdminId] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const [showImport, setShowImport] = useState(false);
  const [showRecord, setShowRecord] = useState(false);

  const { data: staffData } = useHrGetStaffQuery({});
  const staff = staffData?.rows ?? [];

  const {
    data: register,
    isLoading,
    isFetching,
    refetch,
  } = useHrGetAttendanceQuery({ from, to, admin_id: adminId || undefined, status: status || undefined });

  const { data: summary = [], isFetching: summaryFetching } = useHrGetAttendanceSummaryQuery(
    { from, to, admin_id: adminId || undefined },
    { skip: view !== "summary" }
  );

  const rows = useMemo(() => register?.rows ?? [], [register]);

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    if (!nq) return rows;
    return rows.filter((r: any) => staffName(r.admin).toLowerCase().includes(nq));
  }, [rows, q]);

  const totals = useMemo(() => ({
    present: rows.filter((r: any) => r.status === "present").length,
    late: rows.filter((r: any) => r.status === "late").length,
    absent: rows.filter((r: any) => r.status === "absent").length,
    leave: rows.filter((r: any) => r.status === "leave").length,
  }), [rows]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="bg-white border rounded-xl p-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <Clock className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Attendance</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Imported from the biometric device, correctable by hand.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImport(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
            >
              <Upload className="w-4 h-4" /> Import CSV
            </button>
            <button
              onClick={() => setShowRecord(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
            >
              <Plus className="w-4 h-4" /> Record
            </button>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2 rounded-lg border hover:bg-gray-50"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* View switch */}
        <div className="mt-5 inline-flex rounded-lg border overflow-hidden">
          {(["register", "summary"] as View[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2 text-sm capitalize transition-colors ${
                view === v ? "bg-gray-900 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {v}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Staff</label>
            <select
              value={adminId}
              onChange={(e) => setAdminId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
            >
              <option value="">All staff</option>
              {staff.map((s: any) => (
                <option key={s.id} value={s.id}>{staffName(s)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={view === "summary"}
              className="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100 disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">All statuses</option>
              {ATTENDANCE_STATUSES.map((s) => (
                <option key={s} value={s} className="capitalize">{s}</option>
              ))}
            </select>
          </div>
        </div>

        {view === "register" ? (
          <>
            {/* Totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
              {[
                { label: "Present", value: totals.present, tone: "present" },
                { label: "Late", value: totals.late, tone: "late" },
                { label: "Absent", value: totals.absent, tone: "absent" },
                { label: "On leave", value: totals.leave, tone: "leave" },
              ].map((t) => (
                <div key={t.label} className={`border rounded-xl p-3 ${attendanceColor(t.tone)}`}>
                  <div className="text-2xl font-bold">{t.value}</div>
                  <div className="text-xs font-medium opacity-80">{t.label}</div>
                </div>
              ))}
            </div>

            {/* Search */}
            <div className="mt-4 relative max-w-md">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search this register by staff name…"
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>

            {isLoading ? (
              <div className="mt-8 py-10 flex justify-center text-gray-300">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="mt-5 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
                No attendance rows for this period.
              </div>
            ) : (
              <div className="mt-5 border rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                      <th className="px-4 py-2 font-medium">Date</th>
                      <th className="px-4 py-2 font-medium">Staff</th>
                      <th className="px-4 py-2 font-medium">In</th>
                      <th className="px-4 py-2 font-medium">Out</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium text-right">Late</th>
                      <th className="px-4 py-2 font-medium text-right">Worked</th>
                      <th className="px-4 py-2 font-medium">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map((r: any) => (
                      <tr key={r.id} className="hover:bg-gray-50/60">
                        <td className="px-4 py-2.5 text-gray-800 whitespace-nowrap">{fmtDate(r.work_date)}</td>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-gray-900">{staffName(r.admin)}</div>
                          {r.admin?.staff_no && <div className="text-xs text-gray-400">{r.admin.staff_no}</div>}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">{fmtTime(r.clock_in)}</td>
                        <td className="px-4 py-2.5 text-gray-600">{fmtTime(r.clock_out)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs border rounded-full ${attendanceColor(r.status)}`}>
                            {r.status ?? "—"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{fmtMinutes(r.minutes_late)}</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{fmtMinutes(r.minutes_worked)}</td>
                        <td className="px-4 py-2.5">
                          <span className="text-[10px] uppercase tracking-wide text-gray-400">{r.source ?? "—"}</span>
                          {r.note && <div className="text-xs text-gray-400 truncate max-w-[160px]" title={r.note}>{r.note}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          /* Summary */
          summaryFetching ? (
            <div className="mt-8 py-10 flex justify-center text-gray-300">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : summary.length === 0 ? (
            <div className="mt-5 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
              Nothing recorded between {fmtDate(from)} and {fmtDate(to)}.
            </div>
          ) : (
            <div className="mt-5 border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                    <th className="px-4 py-2 font-medium">Staff</th>
                    <th className="px-4 py-2 font-medium text-right">Days</th>
                    <th className="px-4 py-2 font-medium text-right">Present</th>
                    <th className="px-4 py-2 font-medium text-right">Late</th>
                    <th className="px-4 py-2 font-medium text-right">Absent</th>
                    <th className="px-4 py-2 font-medium text-right">Leave</th>
                    <th className="px-4 py-2 font-medium text-right">Lateness</th>
                    <th className="px-4 py-2 font-medium text-right">Hours</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {summary.map((s: any) => (
                    <tr key={s.admin_id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-2.5 font-medium text-gray-900">{s.staff ?? "—"}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{s.days_recorded}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{s.present}</td>
                      <td className="px-4 py-2.5 text-right text-amber-700 font-medium">{s.late}</td>
                      <td className="px-4 py-2.5 text-right text-red-700 font-medium">{s.absent}</td>
                      <td className="px-4 py-2.5 text-right text-sky-700">{s.leave}</td>
                      <td className="px-4 py-2.5 text-right text-gray-600">{fmtMinutes(s.minutes_late)}</td>
                      <td className="px-4 py-2.5 text-right text-gray-800 font-semibold">{s.hours_worked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {showImport && <ImportModal onClose={() => setShowImport(false)} />}
      {showRecord && <RecordModal staff={staff} onClose={() => setShowRecord(false)} />}
    </div>
  );
}

/* ── Biometric CSV import ─────────────────────────────────────────────────── */

function ImportModal({ onClose }: { onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importCsv, { isLoading }] = useHrImportAttendanceMutation();

  const submit = async () => {
    if (!file) return;
    setError(null);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res: any = await importCsv(fd).unwrap();
      setResult(res);
    } catch (err: any) {
      setError(errMsg(err));
      if (err?.data?.result) setResult(err.data.result);
    }
  };

  const unmatched: string[] = result?.result?.unmatched ?? result?.unmatched ?? [];

  return (
    <HrModal title="Import attendance" icon={<Upload className="w-4 h-4 text-indigo-600" />} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Upload the biometric device's CSV export. Rows are matched to staff by their{" "}
          <span className="font-medium">biometric id</span>, so anyone not enrolled is reported back rather than
          imported.
        </p>

        <label className="flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
          <Download className="w-6 h-6 text-gray-400" />
          <span className="text-sm text-gray-600">{file ? file.name : "Choose a .csv or .txt file"}</span>
          <span className="text-xs text-gray-400">Max 10 MB</span>
          <input
            type="file"
            accept=".csv,text/csv,.txt"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); setError(null); }}
            className="hidden"
          />
        </label>

        {error && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {result?.message && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-100 text-xs text-green-700">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            {result.message}
          </div>
        )}

        {unmatched.length > 0 && (
          <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-800">
            <p className="font-semibold mb-1">Unmatched biometric ids ({unmatched.length})</p>
            <p className="font-mono break-all">{unmatched.slice(0, 30).join(", ")}{unmatched.length > 30 ? " …" : ""}</p>
            <p className="mt-1">Set these ids on the matching staff records, then import again.</p>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">
            {result ? "Done" : "Cancel"}
          </button>
          <button
            onClick={submit}
            disabled={!file || isLoading}
            className="flex-1 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            Import
          </button>
        </div>
      </div>
    </HrModal>
  );
}

/* ── Manual entry / correction ────────────────────────────────────────────── */

function RecordModal({ staff, onClose }: { staff: any[]; onClose: () => void }) {
  const [form, setForm] = useState({
    admin_id: "",
    work_date: todayIso(),
    clock_in: "",
    clock_out: "",
    status: "",
    note: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [save, { isLoading }] = useHrSaveAttendanceMutation();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await save({
        admin_id: form.admin_id,
        work_date: form.work_date,
        clock_in: form.clock_in || null,
        clock_out: form.clock_out || null,
        status: form.status || null,
        note: form.note.trim() || null,
      }).unwrap();
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
  const labelCls = "block text-xs font-medium text-gray-600 mb-1";

  return (
    <HrModal title="Record attendance" icon={<CalendarDays className="w-4 h-4 text-indigo-600" />} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-gray-600">
          Recording the same staff member and date again overwrites that day — this is how corrections are made.
        </p>

        <div>
          <label className={labelCls}>Staff *</label>
          <select
            required
            value={form.admin_id}
            onChange={(e) => setForm((f) => ({ ...f, admin_id: e.target.value }))}
            className={inputCls}
          >
            <option value="">Select staff…</option>
            {staff.map((s: any) => (
              <option key={s.id} value={s.id}>{staffName(s)}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Date *</label>
            <input
              type="date"
              required
              value={form.work_date}
              onChange={(e) => setForm((f) => ({ ...f, work_date: e.target.value }))}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Clock in</label>
            <input
              type="time"
              value={form.clock_in}
              onChange={(e) => setForm((f) => ({ ...f, clock_in: e.target.value }))}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Clock out</label>
            <input
              type="time"
              value={form.clock_out}
              onChange={(e) => setForm((f) => ({ ...f, clock_out: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label className={labelCls}>Status</label>
          <select
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            className={inputCls}
          >
            <option value="">Derive from the clock times</option>
            {ATTENDANCE_STATUSES.map((s) => (
              <option key={s} value={s} className="capitalize">{s}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Note</label>
          <input
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="e.g. device offline — recorded from the sign-in sheet"
            className={inputCls}
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            Save
          </button>
        </div>
      </form>
    </HrModal>
  );
}
